import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
  VStack,
  useClipboard,
} from '@chakra-ui/react';
import { MdCheck, MdContentCopy, MdLockClock, MdRefresh } from 'react-icons/md';
import urlConstants from '../../lib/constants/url.constants';
import useToastNotification from '../../lib/hooks/useToastNotification';

const backendBaseUrl = (
  process.env.REACT_APP_API_URL || window.location.origin
).replace(/\/$/, '');

const tempPasswordUrl = (userId: string) =>
  `${backendBaseUrl}${urlConstants.users.tempPassword(userId)}`;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('strong_auth_token');
  if (!token) return {};
  return { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` };
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString();
};

interface Props {
  userId: string;
}

/**
 * Security panel: lets an admin mint a short-lived (24h) numeric password so
 * support can log into the app AS this user without knowing or resetting the
 * real password. The plaintext code is shown exactly once, right after it is
 * generated; after that only the "active until" state is available (the backend
 * stores a bcrypt hash and cannot return the code again).
 */
const TempPasswordPanel = ({ userId }: Props) => {
  const showToast = useToastNotification();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [active, setActive] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [code, setCode] = useState<string>(''); // plaintext, only set right after generate
  const { hasCopied, onCopy, setValue } = useClipboard('');

  const fetchStatus = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetch(tempPasswordUrl(userId), { headers: { ...authHeader() } });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || 'Failed to load temporary password status');
      }
      setActive(Boolean(data?.data?.active));
      setExpiresAt(data?.data?.expiresAt || null);
    } catch (err: any) {
      showToast({
        title: 'Failed',
        description: err?.message || 'Could not load temporary password status',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [userId, showToast]);

  // Reset the shown code and refetch whenever we switch users.
  useEffect(() => {
    setCode('');
    fetchStatus();
  }, [fetchStatus]);

  // Keep the clipboard hook's value in sync with the shown code.
  useEffect(() => {
    setValue(code);
  }, [code, setValue]);

  const handleGenerate = async () => {
    if (!userId) return;
    setGenerating(true);
    try {
      const res = await fetch(tempPasswordUrl(userId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || 'Failed to generate temporary password');
      }
      setCode(data?.data?.tempPassword || '');
      setExpiresAt(data?.data?.expiresAt || null);
      setActive(true);
      showToast({
        title: 'Temporary password generated',
        description: 'Copy it now - it will not be shown again.',
        status: 'success',
      });
    } catch (err: any) {
      showToast({
        title: 'Failed',
        description: err?.message || 'Could not generate temporary password',
        status: 'error',
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box bg='white' border='1px solid' borderColor='gray.200' borderRadius='md' p={5}>
      <HStack mb={1} spacing={2}>
        <Icon as={MdLockClock} color='primary.500' boxSize={5} />
        <Text fontSize='md' fontWeight='bold'>Temporary Password</Text>
      </HStack>
      <Text fontSize='sm' color='gray.500' mb={4}>
        Generate a one-time code so support can sign into the app as this user.
        Valid for 24 hours. The user&apos;s real password is never changed.
      </Text>

      {loading ? (
        <Flex align='center' justify='center' py={8}>
          <Spinner color='primary.500' />
        </Flex>
      ) : code ? (
        // Just generated: show the plaintext once, click-to-copy.
        <VStack align='stretch' spacing={4}>
          <Box
            role='button'
            onClick={onCopy}
            cursor='pointer'
            bg='gray.50'
            border='1px dashed'
            borderColor={hasCopied ? 'green.400' : 'gray.300'}
            borderRadius='md'
            px={5}
            py={4}
            transition='border-color 0.15s'
            _hover={{ borderColor: 'primary.400' }}
          >
            <Flex align='center' justify='space-between'>
              <Text
                fontFamily='mono'
                fontSize='2xl'
                fontWeight='bold'
                letterSpacing='0.35em'
              >
                {code}
              </Text>
              <HStack spacing={1} color={hasCopied ? 'green.500' : 'gray.500'}>
                <Icon as={hasCopied ? MdCheck : MdContentCopy} boxSize={5} />
                <Text fontSize='sm'>{hasCopied ? 'Copied' : 'Copy'}</Text>
              </HStack>
            </Flex>
          </Box>
          <Text fontSize='sm' color='gray.600'>
            Valid until <strong>{formatDate(expiresAt)}</strong>. This code will
            not be shown again - regenerate if you need a new one.
          </Text>
          <Button
            alignSelf='flex-start'
            size='sm'
            variant='outline'
            leftIcon={<MdRefresh />}
            onClick={handleGenerate}
            isLoading={generating}
          >
            Generate New
          </Button>
        </VStack>
      ) : active ? (
        // Active but hidden: we cannot re-display the code (bcrypt hash).
        <VStack align='stretch' spacing={4}>
          <Box bg='green.50' border='1px solid' borderColor='green.200' borderRadius='md' px={4} py={3}>
            <Text fontSize='sm' color='green.800'>
              A temporary password is active until <strong>{formatDate(expiresAt)}</strong>.
              The code itself can&apos;t be shown again - generate a new one to view a code.
            </Text>
          </Box>
          <Button
            alignSelf='flex-start'
            size='sm'
            colorScheme='primary'
            leftIcon={<MdRefresh />}
            onClick={handleGenerate}
            isLoading={generating}
          >
            Generate New
          </Button>
        </VStack>
      ) : (
        // None: offer to generate.
        <VStack align='stretch' spacing={4}>
          <Text fontSize='sm' color='gray.600'>
            This user does not have an active temporary password.
          </Text>
          <Button
            alignSelf='flex-start'
            size='sm'
            colorScheme='primary'
            onClick={handleGenerate}
            isLoading={generating}
          >
            Generate Temporary Password
          </Button>
        </VStack>
      )}
    </Box>
  );
};

export default TempPasswordPanel;
